import React, { useContext } from "react";
import cn from "classnames";
import ResourceCard, {
    ResourceCardProps,
} from "../../../../../../../design-system-components/ResourceCard/ResourceCard";
import { PlaylistStatus, PlaylistWithProgress } from "../../../../type";
import { sessionStore } from "../../../../../../../contexts/SessionContext";
import { defineMessages, IntlShape, useIntl } from "react-intl";
import { ThemeType } from "../../../../../../../interfaces/Theme";
import { playlistMessages } from "../../../EditPlaylist/playlistMessages";
import { commonMessages } from "../../../../../../../utils/messages";
import { Button } from "@evidenceb/athena-common/design-system/Button";
import { useUrl } from "../../../../../../../hooks/usePage";
import { Pagetype } from "../../../../../../../interfaces/Config";
import { ROUTES } from "../../../const";
import capitalize from "lodash/capitalize";
import useTimeFormat from "../../../../../../../hooks/useTimeFormat";

import "./PlaylistCard.scss";

interface PlaylistCardProps {
    playlist: PlaylistWithProgress;
    onPlaylistPress?: () => void;
    status: PlaylistStatus;
    idle?: boolean;
    opts?: Partial<Options>;
    studentId?: string;
    className?: string;
}

type Options = {
    /** @default true */
    rightIcon: boolean;
    /** @default false */
    condensedProgress: boolean;
    /** @default true */
    creationDate: boolean;
    /** @default false */
    absoluteProgress: boolean;
};

export const minimalOptions: Partial<Options> = {
    rightIcon: false,
    condensedProgress: true,
    creationDate: false,
};

const PlaylistCard = ({
    playlist,
    onPlaylistPress,
    status,
    idle = false,
    opts,
    studentId,
    className,
}: PlaylistCardProps) => {
    const {
        session: { theme },
    } = useContext(sessionStore);
    const intl = useIntl();
    const { displayedDateFormat } = useTimeFormat();
    const playlistURL = useUrl(Pagetype.WORKMODE_PLAYLIST);

    const { progression, rightIcon, mainChip, children, titleChips } =
        getStatusParts(
            status,
            intl,
            playlist,
            studentId,
            theme,
            displayedDateFormat,
            playlistURL,
            opts
        );

    return (
        <ResourceCard
            className={cn("playlist-card", className, {
                "playlist-card--idle": idle,
            })}
            name={{
                as: "h3",
                content: playlist.name,
            }}
            mainChip={mainChip}
            titleChips={titleChips}
            clickAction={
                onPlaylistPress
                    ? {
                          label: intl.formatMessage(
                              {
                                  id: "playlist-resource-card-button-label",
                                  defaultMessage:
                                      "Show more about {playlistName}",
                              },
                              { playlistName: playlist.name! }
                          ),
                          onPress: onPlaylistPress,
                          type: "actionableButton",
                      }
                    : undefined
            }
            icon={{
                path: "icon_mode_solo_prof",
                color: "var(--element-teacher-default)",
            }}
            progression={progression}
            rightIcon={rightIcon}
        >
            {children}
        </ResourceCard>
    );
};

export default PlaylistCard;

const getStatusParts = (
    status: PlaylistStatus,
    intl: IntlShape,
    playlist: PlaylistWithProgress,
    studentId: string | undefined,
    theme: ThemeType,
    displayedDateFormat: (date: string | Date) => string,
    playlistURL: string,
    opts?: Partial<Options>
): Pick<
    ResourceCardProps,
    "rightIcon" | "mainChip" | "progression" | "children" | "titleChips"
> => {
    switch (status) {
        case PlaylistStatus.ARCHIVED:
            return {};
        case PlaylistStatus.DRAFT:
            return {
                children: (
                    <div className="playlist-card__draft-content">
                        <p>
                            {capitalize(
                                intl.formatMessage(playlistMessages.resources)
                            )}
                            {intl.formatMessage(commonMessages.colon)}{" "}
                            {playlist.learning_items.length}
                        </p>
                        <Button
                            className="playlist-card__edit-button"
                            label={intl.formatMessage(messages.editPlaylist)}
                            asRouterLink={{
                                to: `${playlistURL}/${ROUTES.edit}/${playlist.learning_set_id}`,
                            }}
                        />
                    </div>
                ),
                titleChips: [
                    {
                        colorMode: theme,
                        input: intl.formatMessage(messages.draft),
                    },
                ],
            };
        case PlaylistStatus.SENT:
            return {
                rightIcon:
                    opts?.rightIcon === false
                        ? undefined
                        : {
                              path: "play_icon",
                              size: "large",
                              clickAction:
                                  playlist.learning_items.length > 0
                                      ? {
                                            type: "internalRedirection",
                                            to: `${playlistURL}/${ROUTES.play}/${playlist.learning_set_id}`,
                                            altTxt: intl.formatMessage(
                                                playlistMessages.playPlaylist,
                                                {
                                                    playlistName: playlist.name,
                                                }
                                            ),
                                        }
                                      : undefined,
                              justifyContent: "flex-end",
                          },
                progression: getProgress(playlist, studentId, intl, opts),
                mainChip:
                    opts?.creationDate === false
                        ? undefined
                        : {
                              colorMode: theme,
                              input: `${intl.formatMessage(messages.createdOn)}
                                              ${displayedDateFormat(
                                                  playlist.created
                                              )}`,
                          },
            };
    }
};

const messages = defineMessages({
    createdOn: {
        id: "playlist-created-on",
        defaultMessage: "Created on",
    },
    averageProgression: {
        id: "playlist-average-progression",
        defaultMessage: "playlist average progression",
    },
    showDetail: {
        id: "playlist-resource-card-button-label",
        defaultMessage: "Show more about {playlistName}",
    },
    editPlaylist: {
        id: "playlist-editPlaylist",
        defaultMessage: "Edit",
    },
    draft: {
        id: "playlist-draftChip",
        defaultMessage: "Draft",
    },
    progress: {
        id: "playlist-progress",
        defaultMessage: "Progress",
    },
});

const getProgress = (
    playlist: PlaylistWithProgress,
    studentId: string | undefined,
    intl: IntlShape,
    opts?: Partial<Options>
) => {
    if (playlist.status.status === PlaylistStatus.DRAFT || !playlist.progress)
        return undefined;

    let label = `${capitalize(
        intl.formatMessage(playlistMessages.resources)
    )}: ${playlist.learning_items.length}`;
    if (opts?.condensedProgress) {
        label = `${intl.formatMessage(messages.progress)}:`;
    }
    let valueLabel = `${capitalize(
        intl.formatMessage(messages.averageProgression)
    )}: ${Math.round(100 * playlist.progress.average_progress)}%`;
    if (opts?.absoluteProgress && studentId) {
        const studentProgress = playlist.progress.classrooms_progress
            .flatMap((classroom) => classroom.students_progress)
            .find((student) => student.id === studentId);
        if (studentProgress) {
            valueLabel = `${studentProgress?.exercises_done}/${playlist.learning_items.length}`;
        }
    }

    return {
        label,
        value: Math.round(100 * playlist.progress.average_progress),
        valueLabel,
        colors: {
            foreground: "var(--element-teacher-default)",
            background: "var(--element-teacher-disabled)",
        },
    };
};
